import type { AircraftDoc, BookingDoc, UserDoc } from './types';
import { TRACKED_REG } from './flightTrack';

export type RuleOutcome = 'auto_confirm' | 'pending_approval' | 'reject';

export interface RuleResult {
  outcome: RuleOutcome;
  reasons: string[];
}

interface EvaluateInput {
  start: Date;
  end: Date;
  aircraft: AircraftDoc;
  lessee: UserDoc;
  existingBookings: BookingDoc[];
  grounded?: boolean; // true when an open grounding squawk (or airworthiness item) is active
  // Set when an EXISTING booking is being edited without moving its start — e.g.
  // "I'm running long, give me another hour". The past-start and lead-time gates
  // are about claiming a new slot; applied here they'd make it impossible to
  // extend a flight that's already underway. Never set when the start moves.
  keepingStart?: boolean;
}

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

/**
 * Lease §7.3: a lessee may reserve up to thirty (30) days ahead. The lease calls this
 * "a uniform administrative scheduling limit applied to all lessees, not a discretionary
 * approval" — worded that way on purpose, so applying it carries no operational-control
 * problem. It used to be a note the app printed and then ignored; now it's the limit the
 * lease says it is. The owner is exempt: §7.3 binds Lessee, and Casey schedules his own
 * trips and maintenance further out.
 */
export const BOOKING_WINDOW_DAYS = 30;

export function evaluateBooking({
  start,
  end,
  aircraft,
  lessee,
  existingBookings,
  grounded,
  keepingStart,
}: EvaluateInput): RuleResult {
  const reasons: string[] = [];

  // Hard rejects ──────────────────────────────────────
  const isOwner = lessee.role === 'admin' || lessee.billingProfile === 'owner';
  if (!isOwner && (start.getTime() - Date.now()) / DAY > BOOKING_WINDOW_DAYS) {
    return {
      outcome: 'reject',
      reasons: [`The schedule opens ${BOOKING_WINDOW_DAYS} days out. Pick a date within the next ${BOOKING_WINDOW_DAYS} days.`],
    };
  }
  if (end <= start) {
    return { outcome: 'reject', reasons: ['End time must be after start time.'] };
  }
  if (keepingStart) {
    // The start is untouched, so only the end is in play — it still has to be ahead
    // of us, or the booking is being shortened into the past.
    if (end.getTime() < Date.now()) {
      return { outcome: 'reject', reasons: ['End time is already in the past.'] };
    }
  } else {
    if (start.getTime() < Date.now() - 5 * 60 * 1000) {
      return { outcome: 'reject', reasons: ['Start time is in the past.'] };
    }

    const leadMinutes = (start.getTime() - Date.now()) / (60 * 1000);
    if (leadMinutes < 30) {
      return {
        outcome: 'reject',
        reasons: ['Same-day bookings need at least 30 minutes lead time before start.'],
      };
    }
  }

  if (aircraft.status !== 'active') {
    return { outcome: 'reject', reasons: [`Aircraft is currently ${aircraft.status}.`] };
  }

  if (aircraft.maintWindowStart && aircraft.maintWindowEnd) {
    const ws = new Date(`${aircraft.maintWindowStart}T00:00:00`);
    const we = new Date(`${aircraft.maintWindowEnd}T00:00:00`);
    if (start < we && end > ws) {
      const fmt = (d: Date) => d.toLocaleDateString([], { month: 'short', day: 'numeric' });
      const lastDay = new Date(we.getTime() - 86400000);
      return {
        outcome: 'reject',
        reasons: [
          `${TRACKED_REG} is at the shop for ${aircraft.maintWindowLabel || 'scheduled maintenance'} ${fmt(ws)}–${fmt(lastDay)}. Pick a date after ${fmt(lastDay)}.`,
        ],
      };
    }
  }
  if (grounded) {
    return {
      outcome: 'reject',
      reasons: ['Aircraft is grounded by an open squawk — the owner must resolve it before it can be booked.'],
    };
  }

  if (aircraft.annualDueDate) {
    const annualEnd = new Date(aircraft.annualDueDate);
    if (end > annualEnd) {
      return {
        outcome: 'reject',
        reasons: [`Booking would run past annual due date (${aircraft.annualDueDate}).`],
      };
    }
  }

  // Conflict check
  const conflicts = existingBookings.filter((b) => {
    if (b.aircraftId !== aircraft.aircraftId) return false;
    if (b.status === 'cancelled' || b.status === 'denied') return false;
    const bs = b.startDateTime.toDate().getTime();
    const be = b.endDateTime.toDate().getTime();
    return start.getTime() < be && end.getTime() > bs;
  });
  if (conflicts.length > 0) {
    return { outcome: 'reject', reasons: ['That slot conflicts with another booking.'] };
  }

  // Lessee status
  if (lessee.status !== 'active') {
    return {
      outcome: 'reject',
      reasons: [`Your account status is ${lessee.status}. Active status is required to book.`],
    };
  }

  // Executed lease required before any booking (Casey, 2026-08-28). Approving a
  // pilot's documents and activating the account is deliberately NOT enough — the
  // signed lease is what creates the obligation to pay and the damage responsibility
  // in §10.3, so nothing gets reserved until it is countersigned and on file.
  // The renter's certificate of insurance is NOT checked here: §11.2 gives 14 days
  // and gates solo flight, not booking (see lib/soloGate.ts).
  // The owner is exempt for the obvious reason: AJM does not lease the airplane
  // to itself, so there is no lease to sign and never will be. Gating Casey's own
  // schedule on a document that cannot exist locked him out of his own aircraft.
  if (!isOwner && !lessee.leaseSignedAt) {
    return {
      outcome: 'reject',
      reasons: ['Your lease has not been signed yet. Booking opens once the signed lease is on file.'],
    };
  }

  // Currency checks
  const today = new Date();
  const medExp = lessee.pilot?.medicalExpiration ? new Date(lessee.pilot.medicalExpiration) : null;
  if (medExp && medExp < today) {
    reasons.push('Medical certificate is expired.');
    return { outcome: 'reject', reasons };
  }

  // Determine if auto-confirm vs pending ──────────────────────────
  const durationHours = (end.getTime() - start.getTime()) / HOUR;
  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();
  // No owner approval — any active pilot books freely. We still surface a few
  // informational notes (overnight / long) but always auto-confirm: §7.5 makes the
  // lessee's notice "for awareness only and not a request for permission", and
  // requiring approval is exactly what would hand the owner operational control.
  if (!sameDay) reasons.push('Multi-day or overnight booking.');
  if (durationHours > 8) reasons.push('Duration exceeds 8 hours.');

  return { outcome: 'auto_confirm', reasons: reasons.length ? reasons : ["You're good to book."] };
}

export function computeAwayFromBaseCharge(args: {
  start: Date;
  end: Date;
  aircraft: AircraftDoc;
  isAwayFromBase: boolean;
}): number {
  // Booking-time estimate only: the hours flown are not known yet, so this returns the
  // FULL nightly minimum as a worst case. Lease §5.3 is a floor — actual Hobbs counts
  // toward it — so the amount finally billed (see lib/billing.ts) is usually lower and
  // is zero on any trip that flies more than the minimum.
  if (!args.isAwayFromBase) return 0;
  const nights = countCalendarNights(args.start, args.end);
  return nights * args.aircraft.awayFromBaseHoursPerDay * args.aircraft.hourlyRate;
}

export function countCalendarNights(start: Date, end: Date): number {
  // Count midnights between departure evening and return morning.
  const s = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const e = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.max(0, Math.round((e.getTime() - s.getTime()) / DAY));
}
